import { CalendarOutlined } from '@ant-design/icons'
import { useDeliveryRequests } from '@api/useDeliveryRequests'
import { useFormatter } from '@api/useFormatter'
import { useOrders } from '@api/useOrders'
import {
  ErrorNotification,
  GraviButton,
  Horizontal,
  NotificationMessage,
  Texto,
  Vertical,
  addCommasToNumber,
} from '@gravitate-js/excalibrr'
import { Form, Input, Modal } from 'antd'
import moment from 'moment-timezone'
import React, { useEffect, useMemo, useState } from 'react'
import { SiteSearchSelect } from './SiteSearchSelect'
import type { SiteMetadata } from '@/api/useDeliveryRequests/response'
import { DeliveryWindow } from './DeliveryWindow'
import { DeliveryWindowBySelection } from './DeliveryWindowBySelection'
import { Products } from './Products'

type RequestModalProps = {
  visible: boolean
  setVisible: (visible: boolean) => void
  selectedRequest: any
  setSelectedRequest: (request: any) => void
  siteMetadata: SiteMetadata[]
  siteOptions: { label: string; value: string }[]
}

export function RequestModal({
  visible,
  setVisible,
  selectedRequest,
  setSelectedRequest,
  siteMetadata,
  siteOptions,
}: RequestModalProps) {
  const [totalVolume, setTotalVolume] = useState(0)
  const [maxNumberOfProducts, setMaxNumberOfProducts] = useState(false)
  const [requestedProducts, setRequestedProducts] = useState(null)
  const [selectedFromDate, setSelectedFromDate] = useState(false)
  const [selectedToDate, setSelectedToDate] = useState(false)
  const [selectedFromTime, setSelectedFromTime] = useState(null)
  const [selectedSite, setSelectedSite] = useState(null)
  const [volumeError, setVolumeError] = useState(null)
  const [byProductEntry, setByProductEntry] = useState(false)
  const [compartmentDefaultVolumes, setCompartmentDefaultVolumes] = useState(null)
  const [isMultipleDays, setIsMultipleDays] = useState(false)
  const [editingWindow, setEditingWindow] = useState(false)

  const { upsertRequestMutation } = useDeliveryRequests()
  const [form] = Form.useForm()
  const { formatVolume } = useFormatter()
  const formattedVolume = formatVolume(null, true)

  const handleCancel = () => {
    setVisible(false)
    setSelectedRequest(null)
    form.resetFields()
    setRequestedProducts(null)
    setTotalVolume(0)
    setSelectedSite(null)
    setSelectedFromDate(null)
    setSelectedToDate(null)
    setSelectedFromTime(null)
    setVolumeError(null)
    setByProductEntry(false)
    setMaxNumberOfProducts(false)
    setCompartmentDefaultVolumes(null)
    setIsMultipleDays(false)
    setEditingWindow(false)
  }

  const { useDefaultCompartmentConfig, trimCompartmentMutation } = useOrders()
  const { data: defaultCompartmentConfig } = useDefaultCompartmentConfig(
    selectedSite?.market,
    selectedSite?.site_number
  )

  const getTrimmedVolumes = async () => {
    const compartmentsPayload = requestedProducts.map((product) => ({
      compartment_index: product.compartment,
      product_short_name: null,
      product_name: product.product_name,
      volume: product.quantity,
      targets: [
        {
          location_name: selectedSite.site_number,
          tank_id: selectedSite.tanks.find((tank) => tank.product === '87')?.tank_id,
          volume: product.quantity,
          is_error: false,
        },
      ],
    }))
    try {
      const res = await trimCompartmentMutation.mutateAsync({
        compartments: compartmentsPayload,
        first_store_number: selectedSite.site_number,
        market: selectedSite.market,
        trailer_config: defaultCompartmentConfig?.trailer_config,
      })
      const list = res?.map((item) => item.volume)
      setCompartmentDefaultVolumes(list)
      setTotalVolume(list.reduce((acc, curr) => acc + parseInt(curr || 0), 0))
      setRequestedProducts(requestedProducts.map((product, index) => ({ ...product, quantity: list[index] })))
    } catch (e) {
      console.error('Could not trim compartments', { e })
      NotificationMessage('Error', 'Could not trim compartments', true)
    }
  }

  const sendRequest = async (objectToSend) => {
    try {
      await upsertRequestMutation.mutateAsync(objectToSend)
      NotificationMessage('Request Saved', 'Your request has been saved.', false)
    } catch (e) {
      console.error({ e })
      NotificationMessage('Request Not Saved', 'Your request could not be saved.', true)
    } finally {
      handleCancel()
    }
  }
  const getNumberFromTimeString = (timeItem: string | number) => {
    if (typeof timeItem === 'number') return timeItem
    if (typeof timeItem === 'string') {
      const num = parseInt(timeItem?.split(':')[0])
      return timeItem?.includes('PM') ? (num === 12 ? 12 : num + 12) : timeItem.includes('AM') && num === 12 ? 0 : num
    }
  }

  const getFormDates = (fromData, toData, timezone) => {
    let toDate = moment(toData)
    let fromDate = moment(fromData)
    if (timezone) {
      toDate = moment(toData).tz(timezone)
      fromDate = moment(fromData).tz(timezone)
    }
    const fromTime = fromDate?.format('h:mm A')
    const toTime = toDate?.format('h:mm A')
    return { fromDate, fromTime, toDate, toTime }
  }
  const getPayloadDates = (fromDate, fromTime, toDate, toTime, selectedWindow) => {
    const startMoment = moment(fromDate)
    const endMoment = selectedWindow?.value ? moment(fromDate) : moment(toDate)

    if (selectedWindow?.value) {
      const [startTimeStr, endTimeStr] = selectedWindow.value.split('|')
      startMoment.hours(moment(startTimeStr).hours())
      endMoment.hours(moment(endTimeStr).hours())

      if (endMoment.isBefore(startMoment)) {
        endMoment.add(1, 'day') // Add a day if crossing midnight
      }
    } else {
      startMoment.hours(getNumberFromTimeString(fromTime))
      endMoment.hours(getNumberFromTimeString(toTime))
    }

    return {
      delivery_window_start: startMoment.toISOString(true),
      delivery_window_end: endMoment.toISOString(true),
    }
  }

  const normalizeCompartments = (formData) => {
    const compartments = Object.keys(formData)
      ?.filter((key) => key.includes('compartment') && formData[key])
      .map((key) => {
        const product = selectedSite.products.find((product) => product.name === formData[key])
        const island = selectedSite.tanks.find((tank) => tank.product === product?.name)?.island
        const compartment = parseInt(key.split(' ')[1])
        const volumeList = compartmentDefaultVolumes || defaultCompartmentConfig?.default
        const quantity = volumeList?.[compartment]
        return {
          product_id: product?.id,
          product_name: formData[key],
          quantity,
          island,
          compartment,
          compartment_index: compartment,
        }
      })
    setRequestedProducts(compartments)
    return compartments
  }
  const handleCompartmentChange = async (data) => {
    const filledCompartments = Object.keys(data)?.filter((key) => key.includes('compartment') && data[key])
    const volumeList = compartmentDefaultVolumes || defaultCompartmentConfig?.default
    const compartments = filledCompartments.map((compartment) => volumeList?.[compartment?.split(' ')?.[1]])
    const total = compartments.reduce((acc, curr) => acc + parseInt(curr || 0), 0)
    setTotalVolume(total)
    form.setFieldsValue({ totalVolume: total })
    normalizeCompartments(data)
  }
  const normalizeProducts = (formData) =>
    Object.keys(formData)
      .filter((key) => key.includes('product') && formData[key])
      .map((key) => {
        const product = selectedSite.products.find((product) => product.id === key.split(' ')[1])
        const island = selectedSite.tanks.find((tank) => tank.product === product?.name)?.island
        return {
          product_id: product?.id,
          product_name: product?.name,
          quantity: formData[key] || 0,
          island,
        }
      })
  const handleProductChange = (data) => {
    const productVolumes = Object.keys(data)
      ?.filter((key) => key.includes('product') && data[key])
      ?.map((key) => data[key])
    const total = productVolumes.reduce((acc, curr) => acc + parseInt(curr || 0), 0)
    setTotalVolume(total)
    form.setFieldsValue({ totalVolume: total })

    const products = normalizeProducts(data).filter((product) => product.quantity > 0)
    const changedToZero = normalizeProducts(data).filter((product) => product.quantity === 0)
    changedToZero.forEach((product) => {
      const field = `product ${product.product_id} ${product.island}`
      form.setFieldsValue({ [field]: null })
    })
    setRequestedProducts(products)
    if (products.length === selectedSite.max_number_of_products && byProductEntry) {
      setMaxNumberOfProducts(true)
    } else setMaxNumberOfProducts(false)
  }
  const setAdjustedDeliveryWindow = (fromDateTime, order_request_window_length, fromTime) => {
    const adjuster = moment(fromDateTime)
    const adjustedToDateTime = adjuster.add(order_request_window_length, 'hours')
    const adjustedToTime = adjustedToDateTime.hours()
    form.setFieldsValue({ toDate: adjustedToDateTime, toTime: adjustedToTime })
    if (!adjustedToDateTime.isSame(fromDateTime, 'day')) {
      setIsMultipleDays(true)
    }
    setNewWindow(fromDateTime, fromTime, adjustedToDateTime)
  }
  const setNewWindow = (fromDate, fromTime, toDate, setSingleDay = false) => {
    setSelectedFromDate(fromDate)
    setSelectedToDate(toDate)
    setSelectedFromTime(fromTime)
    if (!isMultipleDays || setSingleDay) {
      setIsMultipleDays(!fromDate.isSame(toDate, 'day'))
    }
  }
  const checkDeliveryWindow = (fromTime, fromDate, toTime, toDate, order_request_window_length) => {
    if ((!fromTime && fromTime !== 0) || !fromDate || (!toTime && toTime !== 0) || !toDate) {
      return setNewWindow(fromDate, fromTime, toDate)
    }
    const fromDateTime = moment(fromDate).hours(fromTime).minutes(0)
    const toDateTime = moment(toDate).hours(toTime).minutes(0)
    const diffInHours = toDateTime.diff(fromDateTime, 'hours')
    if (diffInHours < order_request_window_length) {
      setAdjustedDeliveryWindow(fromDateTime, order_request_window_length, fromTime)
    } else {
      setNewWindow(fromDate, fromTime, toDate)
    }
  }
  const handleFromTimeChange = (fromTime, data) => {
    setSelectedFromTime(fromTime)
    if (!fromTime && fromTime !== 0) return
    const { fromDate, toDate, toTime } = data
    const toTimeNumber = getNumberFromTimeString(toTime)
    const fromTimeNumber = getNumberFromTimeString(fromTime)
    checkDeliveryWindow(fromTimeNumber, fromDate, toTimeNumber, toDate, selectedSite?.order_request_window_length)
  }

  const handleToTimeChange = (toTime, data) => {
    if (!toTime) return
    const { fromDate, toDate, fromTime } = data
    const toTimeNumber = getNumberFromTimeString(toTime)
    const fromTimeNumber = getNumberFromTimeString(fromTime)
    checkDeliveryWindow(fromTimeNumber, fromDate, toTimeNumber, toDate, selectedSite?.order_request_window_length)
  }

  const handleFromDateChange = (fromDate, data) => {
    if (!fromDate) {
      setSelectedFromDate(null)
      return
    }
    if (fromDate.isSame(selectedSite?.order_request_window_start, 'day')) {
      const startHour = moment(selectedSite.order_request_window_start).tz(selectedSite?.timezone).hour()
      if ((!selectedFromTime && selectedFromTime !== 0) || getNumberFromTimeString(selectedFromTime) < startHour) {
        setSelectedFromTime(startHour)
        form.setFieldsValue({ fromTime: startHour })
        setSelectedFromTime(startHour)
      }
    }
    checkDeliveryWindow(data.fromTime, fromDate, data.toTime, data.toDate, selectedSite?.order_request_window_length)
  }

  const handleToDateChange = (toDate, data) => {
    if (!toDate) {
      setSelectedToDate(null)
      return
    }
    checkDeliveryWindow(data.fromTime, data.fromDate, data.toTime, toDate, selectedSite?.order_request_window_length)
  }
  const onValuesChange = (changed, data) => {
    if (volumeError) {
      setVolumeError(null)
      form.getFieldsError().forEach((field) => form.setFields([{ name: field.name, errors: [] }]))
    }
    const changedValue = Object.keys(changed)[0]
    if (changedValue === 'fromDate') return handleFromDateChange(changed.fromDate, data)
    if (changedValue === 'toDate') return handleToDateChange(changed.toDate, data)
    if (changedValue === 'fromTime') return handleFromTimeChange(changed.fromTime, data)
    if (changedValue === 'toTime') return handleToTimeChange(changed.toTime, data)
    const productsChanged = changedValue?.split(' ')?.[0] === 'product'
    if (productsChanged) return handleProductChange(data)
    const compartmentsChanged = changedValue?.split(' ')?.[0] === 'compartment'
    if (compartmentsChanged) return handleCompartmentChange(data)
  }

  const setUpDefaultDates = (site) => {
    if (!moment(site.order_request_window_start).isSame(moment(site.order_request_window_end), 'day')) {
      setIsMultipleDays(true)
    } else {
      setIsMultipleDays(false)
    }
    const { fromDate, fromTime, toDate, toTime } = getFormDates(
      site.order_request_window_start,
      site.order_request_window_end,
      site.timezone
    )
    form.setFieldsValue({
      fromDate,
      fromTime,
      toDate,
      toTime,
    })
    setSelectedFromDate(fromDate)
    setSelectedToDate(toDate)
    setSelectedFromTime(fromTime)
  }

  const handleSiteSelected = (siteId) => {
    form.setFieldsValue({ totalVolume: 0 })
    setTotalVolume(0)
    setVolumeError(null)
    setCompartmentDefaultVolumes(null)
    setByProductEntry(false)

    const productFields = Object.keys(form.getFieldsValue()).filter(
      (key) => key.includes('product') || key.includes('compartment')
    )
    productFields.forEach((field) => form.setFields([{ name: field, value: null }]))
    form.setFieldsValue({ totalVolume: 0 })
    setRequestedProducts(null)

    const site = siteMetadata?.find((site) => site.site_id === siteId)
    if (!site) {
      setSelectedSite(null)
      return
    }
    setSelectedSite(site)
    setUpDefaultDates(site)
  }

  const siteAddress = useMemo(() => {
    if (!selectedSite) return ''
    return `${selectedSite.address}, ${selectedSite.city}, ${selectedSite.state}`
  }, [selectedSite])
  const productsList = useMemo(() => {
    if (!selectedSite) return []
    const list = selectedSite.tanks.map((tank) => {
      const product = selectedSite.products.find((product) => product.name === tank.product)
      return {
        product_id: product?.id,
        product_name: tank.product,
        island: tank.island,
      }
    })
    // filter out duplicate product id and island combinations
    return list.filter((product, index) => {
      const foundIndex = list.findIndex(
        (item) => item.product_id === product.product_id && item.island === product.island
      )
      return foundIndex === index
    })
  }, [selectedSite])

  const checkForm = (formData) => {
    const productFields = Object.keys(formData).filter((key) => key.includes('product'))

    if (totalVolume < selectedSite.min_volume && !selectedSite?.counterparty_allows_short_loads && byProductEntry) {
      setVolumeError('min_volume')
      productFields.forEach((field) => form.setFields([{ name: field, errors: ['Required min not met.'] }]))
      return
    }
    if (totalVolume > selectedSite.max_volume && byProductEntry) {
      productFields.filter((key) => formData[key] && formData[key] > 0)
      setVolumeError('max_volume')
      productFields.forEach((field) => form.setFields([{ name: field, errors: ['Total volume exceeds site limit.'] }]))
      return
    }

    if (
      !selectedSite?.counterparty_allows_short_loads &&
      (!requestedProducts || requestedProducts?.length < selectedSite?.max_number_of_products) &&
      !byProductEntry
    ) {
      setVolumeError('compartments_not_filled')
      const compartmentFields = Object.keys(formData).filter((key) => key.includes('compartment'))
      compartmentFields.forEach((field) => form.setFields([{ name: field, errors: ['Please fill all compartments.'] }]))
      form.setFields([{ name: 'totalVolume', errors: ['Please fill all compartments.'] }])
      return
    }
    handleUpsert(formData)
  }
  const handleErrorNotification = (error) => {
    ErrorNotification(error)

    const item = error.errorFields.map((item) => item.name?.[0])
    if (item.includes('totalVolume') && !selectedSite?.counterparty_allows_short_loads && byProductEntry) {
      setVolumeError('min_volume')
    }
  }
  const handleUpsert = (formData) => {
    const prosToSend = byProductEntry ? normalizeProducts(formData) : normalizeCompartments(formData)

    const newDates =
      !editingWindow && selectedRequest
        ? {
            delivery_window_start: selectedRequest.delivery_window_start,
            delivery_window_end: selectedRequest.delivery_window_end,
          }
        : getPayloadDates(
            formData.fromDate,
            formData.fromTime,
            formData.toDate,
            formData.toTime,
            formData.selectedWindow
          )

    const baseObject = {
      ...formData,
      site_id: selectedSite?.site_id,
      site_name: selectedSite?.site_number,
      site_address: selectedSite?.address,
      site_city: selectedSite?.city,
      site_state: selectedSite?.state,
      customer_id: selectedSite?.counterparty_id,
      customer_name: selectedSite?.counterparty_name,
      delivery_window_start: newDates.delivery_window_start,
      delivery_window_end: newDates.delivery_window_end,
      products: prosToSend,
      total_volume: totalVolume,
    }

    if (selectedRequest) {
      Object.assign(baseObject, {
        id: selectedRequest.id,
        number: selectedRequest.number,
        status: selectedRequest.status,
        created_at: selectedRequest.created_at,
      })
    } else {
      baseObject.status = 'Pending'
    }

    if (!byProductEntry) {
      baseObject.trailer_config = defaultCompartmentConfig?.trailer_config
    }

    sendRequest(baseObject)
  }

  useEffect(() => {
    if (visible) {
      if (selectedRequest) {
        const site = siteMetadata?.find((site) => site.site_number === selectedRequest.site_name)
        if (!site) {
          const error = {
            errorFields: [
              {
                errors: [
                  `Could not find site #${selectedRequest.site_name} in the list of Call In Sites. Please select a site from the list to proceed.`,
                ],
              },
            ],
          }
          ErrorNotification(error)
        } else {
          setSelectedSite(site)
          const { fromDate, fromTime, toDate, toTime } = getFormDates(
            moment(selectedRequest.delivery_window_start),
            moment(selectedRequest.delivery_window_end),
            undefined
          )
          form.setFieldsValue(selectedRequest)
          form.setFieldsValue({
            fromDate,
            fromTime,
            toDate,
            toTime,
          })
          setSelectedFromTime(fromTime)
          setIsMultipleDays(!fromDate.isSame(toDate, 'day'))
          if (selectedRequest?.products?.length && selectedRequest.products[0]?.compartment_index !== null) {
            const productsToSet = selectedRequest.products.map((product) => ({
              ...product,
              compartment: product.compartment_index,
            }))
            setRequestedProducts(productsToSet)
            selectedRequest.products.forEach((product) => {
              const field = `compartment ${product.compartment_index}`
              form.setFieldsValue({ [field]: product.product_name })
            })
            setByProductEntry(false)
          } else if (site.allow_by_product_request) {
            setByProductEntry(true)
            selectedRequest.products.forEach((product) => {
              const field = `product ${product.product_id} ${product.island}`
              form.setFieldsValue({ [field]: product.quantity })
            })
            setRequestedProducts(selectedRequest.products)
          }

          form.setFieldsValue({ totalVolume: selectedRequest.total_volume })
          setTotalVolume(selectedRequest.total_volume)
        }
      }
    }
  }, [visible])

  useEffect(() => {
    if (
      visible &&
      !byProductEntry &&
      selectedSite?.max_number_of_products &&
      requestedProducts?.length > 0 &&
      requestedProducts?.length === selectedSite?.max_number_of_products
    ) {
      getTrimmedVolumes()
    }
  }, [requestedProducts?.length, selectedSite?.max_number_of_products, visible, byProductEntry])

  return (
    <Form form={form} onFinish={checkForm} onFinishFailed={handleErrorNotification} onValuesChange={onValuesChange}>
      <Modal
        destroyOnClose
        width={700}
        visible={visible}
        onCancel={handleCancel}
        title='Order Request'
        footer={<ModalFooter form={form} handleCancel={handleCancel} selectedRequest={selectedRequest} />}
      >
        <Horizontal alignItems='center' className='pt-3'>
          <div className='p-4 mr-4' style={{ background: 'var(--theme-color-2)', borderRadius: '50%' }}>
            <CalendarOutlined style={{ fontSize: 24, color: 'var(--gray-100)' }} />
          </div>
          <Vertical>
            <Texto category='h3'>Order Request {selectedRequest && selectedRequest?.number}</Texto>
            <Texto appearance='hint' category='p2'>
              Please specify destination, delivery window, product and volume.
            </Texto>
          </Vertical>
        </Horizontal>

        <hr className='delivery-request-hr mt-4 mb-4' />

        <Horizontal className='py-2 full-height-width' verticalCenter justifyContent='space-between'>
          <Vertical verticalCenter>
            <Texto align='left'>Site</Texto>
          </Vertical>
          <Vertical alignItems='flex-end' verticalCenter>
            <Form.Item
              className='m-0'
              name='site_name'
              rules={[{ required: true, message: 'Destination is required.' }]}
            >
              <SiteSearchSelect
                options={siteOptions}
                site={selectedSite}
                onChange={handleSiteSelected}
                setSite={setSelectedSite}
              />
            </Form.Item>
          </Vertical>
        </Horizontal>

        <Horizontal className='py-2 full-height-width' alignItems='center' justifyContent='space-between'>
          <Vertical alignItems='flex-end'>
            <Texto align='left'>{selectedSite?.counterparty_name || ''}</Texto>
          </Vertical>
        </Horizontal>
        <Horizontal className='py-2 pt-4 full-height-width' alignItems='center' justifyContent='space-between'>
          <Vertical>
            <Texto align='left'>Address</Texto>
          </Vertical>
          <Vertical alignItems='flex-end'>
            <Texto align='left'>{siteAddress || ''}</Texto>
          </Vertical>
        </Horizontal>

        {/* Delivery Window */}
        {selectedSite?.fixed_windows ? (
          <DeliveryWindowBySelection
            selectedSite={selectedSite}
            selectedDate={form.getFieldValue('fromDate')}
            selectedWindow={form.getFieldValue('selectedWindow')}
          />
        ) : (
          <DeliveryWindow
            form={form}
            selectedSite={selectedSite}
            selectedFromTime={selectedFromTime}
            selectedFromDate={selectedFromDate}
            selectedToDate={selectedToDate}
            isMultipleDays={isMultipleDays}
            setIsMultipleDays={setIsMultipleDays}
            setUpDefaultDates={setUpDefaultDates}
            getNumberFromTimeString={getNumberFromTimeString}
            setSelectedToDate={setSelectedToDate}
            editingWindow={editingWindow}
            setEditingWindow={setEditingWindow}
            selectedRequest={selectedRequest}
          />
        )}
        <hr className='delivery-request-hr mt-4 mb-4' />

        {/* Products */}
        <Products
          formattedVolume={formattedVolume}
          productsList={productsList}
          volumeError={volumeError}
          setVolumeError={setVolumeError}
          maxNumberOfProducts={maxNumberOfProducts}
          requestedProducts={requestedProducts}
          setRequestedProducts={setRequestedProducts}
          compartmentDefaultVolumes={compartmentDefaultVolumes || defaultCompartmentConfig?.default}
          form={form}
          handleCompartmentChange={handleCompartmentChange}
          byProductEntry={byProductEntry}
          setByProductEntry={setByProductEntry}
          setTotalVolume={setTotalVolume}
          setMaxNumberOfProducts={setMaxNumberOfProducts}
          selectedSite={selectedSite}
        />

        {/* Total Volume */}
        <Horizontal alignItems='center' className='py-2' justifyContent='space-between'>
          <Vertical justifyContent='start'>
            <Texto align='left' textTransform='uppercase' category='h5'>
              Total Volume
            </Texto>
          </Vertical>
          <Vertical alignItems='end'>
            <Horizontal justifyContent='start' alignItems='end' className='pb-1'>
              <Texto
                align='right'
                category='h2'
                style={{ marginBottom: -7 }}
                appearance={volumeError === 'min_volume' ? 'error' : 'default'}
              >
                {addCommasToNumber(totalVolume, 0)}
              </Texto>
              <Texto
                textTransform='uppercase'
                appearance={volumeError === 'max_volume' ? 'error' : 'default'}
                align='right'
                className='ml-1'
                category='h3'
              >
                /{addCommasToNumber(selectedSite?.max_volume) ?? 0} {formattedVolume}s
              </Texto>
            </Horizontal>
            <Horizontal>
              {selectedSite && (
                <Texto
                  align='right'
                  style={{ fontStyle: 'italic' }}
                  appearance={volumeError === 'min_volume' ? 'error' : 'default'}
                >
                  Required min of {addCommasToNumber(selectedSite?.min_volume, 0)} {formattedVolume}s
                </Texto>
              )}
              {volumeError === 'max_volume' && (
                <Texto align='right' appearance='error' className='ml-2'>
                  Total volume is greater than {addCommasToNumber(selectedSite?.max_volume) ?? 0} {formattedVolume}s.
                </Texto>
              )}
              {volumeError === 'compartments_not_filled' && (
                <Texto align='right' appearance='error' className='ml-2'>
                  Please fill all compartments.
                </Texto>
              )}
            </Horizontal>
          </Vertical>
        </Horizontal>
        <Horizontal justifyContent='end'>
          {selectedSite && byProductEntry && (
            <Texto align='right'>Max Product Combinations: {selectedSite?.max_number_of_products}</Texto>
          )}
        </Horizontal>
        <hr className='delivery-request-hr ' />

        <Horizontal gap={64} className=' pt-3 full-height-width' verticalCenter>
          <Texto className='pb-4' style={{ whiteSpace: 'nowrap' }}>
            Notes
          </Texto>
          <Form.Item name='customer_note' className='full-height-width'>
            <Input.TextArea
              showCount
              maxLength={300}
              autoSize={{ minRows: 2, maxRows: 5 }}
              className='full-height-width'
              placeholder='Enter Delivery Notes'
            />
          </Form.Item>
        </Horizontal>
      </Modal>
      <Form.Item
        name='totalVolume'
        hidden
        rules={[
          {
            required: true,
            message: selectedSite
              ? `Required min of ${addCommasToNumber(selectedSite?.min_volume, 0)} ${formattedVolume}s`
              : 'Product and Volume are required.',
          },
        ]}
      />
    </Form>
  )
}

function ModalFooter({ handleCancel, form, selectedRequest }) {
  return (
    <Horizontal justifyContent='flex-end' gap={16}>
      <GraviButton buttonText='Cancel' onClick={handleCancel} style={{ borderRadius: '5px', width: '80px' }} />
      <GraviButton
        theme2
        buttonText={selectedRequest ? 'Save' : 'Create'}
        onClick={form.submit}
        style={{ borderRadius: '5px', width: '80px' }}
      />
    </Horizontal>
  )
}
